import React from "react"
import Samples from "./Samples"
import Carousel from "./Carousel"
import TagFilters from "./TagFilters"
import { getCsrfToken } from "../utils/userUtils"
import { getTagOptionsForArrayOfTagNames } from "../utils/tagUtils"
import axios from "axios"
import { observer, Provider } from "mobx-react"
import { action, runInAction } from "mobx"
import sampleStore from "../stores/sampleStore"
import Sample from "../models/sample"
import { Fade, LinearProgress } from "@mui/material"
import { styled } from "@mui/material/styles"
import _ from "lodash"

const stores = {
  sampleStore,
}

const DividerContainer = styled("div")`
  padding: 0 10px;
  height: 5px;
`

const CollectionsCarouselContainer = styled("div")`
  margin: 20px 10px;
`

const DEFAULT_TEMPO_RANGE_ARRAY = [0, 200]

@observer
export default class SamplesWithTagFilter extends React.Component {
  constructor(props) {
    super(props)
    this.state = this.initializeStateFromProps(props)
    sampleStore.samples = props.samples.map((sample) => new Sample(sample))
  }

  componentDidMount() {
    // Load selectedSamples on the client side after hydration
    sampleStore.loadFromLocalStorage();
  }

  initializeStateFromProps(props) {
    let tempoRangeArray
    if (props.minTempo && props.maxTempo) {
      tempoRangeArray = [Number(props.minTempo), Number(props.maxTempo)]
    } else {
      tempoRangeArray = DEFAULT_TEMPO_RANGE_ARRAY
    }

    let durationRangeArray
    if (props.minDuration && props.maxDuration) {
      durationRangeArray = [
        Number(props.minDuration),
        Number(props.maxDuration),
      ]
    } else {
      const initialDurationRangeArray = [0, Number(props.longestSampleDuration)]
      durationRangeArray = initialDurationRangeArray
    }

    // have to set the selected options passed via params
    const additional_tags_options = getTagOptionsForArrayOfTagNames(
      props.tagOptions,
      props.additionalTags
    )

    // Since tagOptions won't included excluded tags, since we asked them to be excluded
    // We need to build the option object here ourselves
    const exclude_tags_options = props.excludeTags?.map((tagName) => {
      return { label: tagName, value: tagName }
    })

    return {
      pagyMetadata: props.pagyMetadata,
      formattedSampleCount: props.formattedSampleCount,
      tagOptions: props.tagOptions,
      isLoading: false,
      tempoRangeArray: tempoRangeArray,
      durationRangeArray: durationRangeArray,
      additional_tags: props.additionalTags,
      exclude_tags: props.excludeTags,
      additional_tags_options: additional_tags_options,
      exclude_tags_options: exclude_tags_options,
      key: props.selectedKey,
      mode: props.selectedMode,
      sortBy: props.sortBy,
    }
  }

  getSampleParams() {
    const {
      additional_tags,
      exclude_tags,
      tempoRangeArray,
      durationRangeArray,
      key,
      mode,
      sortBy,
    } = this.state

    const params = new URL(window.location).searchParams
    const searchParam = params.get("search")
    const sampleParams = {
      tags: additional_tags,
      exclude_tags,
      min_tempo: tempoRangeArray[0],
      max_tempo: tempoRangeArray[1],
      min_duration: durationRangeArray[0],
      max_duration: durationRangeArray[1],
      key,
      mode,
      sort_by: sortBy,
      search: searchParam,
    }
    return sampleParams
  }

  @action
  loadSamples = async () => {
    this.setState({ isLoading: true })
    axios.defaults.headers.common["Accept"] = "application/json"
    axios.defaults.headers.common["X-CSRF-Token"] = getCsrfToken()

    const pathURL = window.location.pathname
    const sampleParams = this.getSampleParams()
    try {
      const response = await axios({
        url: pathURL,
        responseType: "json",
        params: sampleParams,
      })

      runInAction(() => {
        if (response.data.pagyMetadata) {
          // If pagyMetadata has changed because of filter modification we need to update it
          this.setState({ pagyMetadata: response.data.pagyMetadata })
        }
        sampleStore.resetStore()
        response.data.samples.forEach((sampleJSON) => {
          const sample = new Sample(sampleJSON)
          sampleStore.addSample(sample)
        })
        this.setState({
          formattedSampleCount: response.data.formattedSampleCount,
          tagOptions: response.data.tagOptions,
          isLoading: false,
        })

        // Update the browser's URL bar with the ajax query just performed
        // but do that without reload
        const axiosUri = axios.getUri({
          url: pathURL,
          responseType: "json",
          params: sampleParams,
        })
        window.history.pushState({}, "", axiosUri)
      })

      return true
    } catch (error) {
      console.error("getSamples error", error)
      return false
    }
  }

  onTagFilterChange = (filterValues) => {
    this.setState({ ...filterValues }, this.loadSamples)
  }

  onDurationSliderChange = (filterValues) => {
    this.setState({ ...filterValues })
  }

  onDurationSliderChangeCommitted = (filterValues) => {
    this.loadSamples()
  }

  onTempoSliderChange = (filterValues) => {
    this.setState({ ...filterValues })
  }

  onTempoSliderChangeCommitted = (filterValues) => {
    this.loadSamples()
  }

  handleSelectTagsChange = (selectedOptionsArray, event) => {
    const selectedTags = selectedOptionsArray
      ? selectedOptionsArray.map((optionObject) => optionObject.value)
      : []
    const selectName = event.name
    this.setState(
      {
        // An array of tag names selected for the react-select field
        [selectName]: selectedTags,
        // An array of tag options selected for the react-select field, so that it
        // can be used as a controlled component
        [`${selectName}_options`]: selectedOptionsArray,
      },
      this.loadSamples
    )
  }

  areFiltersPresent = () => {
    const {
      exclude_tags,
      tags,
      mode,
      key,
      tempoRangeArray,
      durationRangeArray,
    } = this.state
    const { longestSampleDuration } = this.props

    const initialDurationRangeArray = [0, Number(longestSampleDuration)]

    if (
      (exclude_tags && exclude_tags.length > 0) ||
      (tags && tags.length > 0) ||
      (mode && mode.length > 0) ||
      (key && key.length > 0) ||
      !_.isEqual(tempoRangeArray, DEFAULT_TEMPO_RANGE_ARRAY) ||
      !_.isEqual(durationRangeArray, initialDurationRangeArray)
    ) {
      return true
    } else {
      return false
    }
  }

  render() {
    const {
      headerText,
      searchParam,
      sortOptions,
      collections,
      shortestSampleDuration,
      longestSampleDuration,
      pageType
    } = this.props
    const {
      pagyMetadata,
      tempoRangeArray,
      durationRangeArray,
      formattedSampleCount,
      tagOptions,
      isLoading,
      key,
      mode,
      sortBy,
      additional_tags_options,
      exclude_tags_options,
    } = this.state
    const shouldShowCollections = !this.areFiltersPresent()
    const showSampleSelect = sampleStore.showSampleSelect
    return (
      <Provider {...stores}>
        <TagFilters
          pageType={pageType}
          headerText={headerText}
          searchParam={searchParam}
          sortOptions={sortOptions}
          tagOptions={tagOptions}
          formattedSampleCount={formattedSampleCount}
          onTagFilterChange={this.onTagFilterChange}
          onTempoSliderChange={this.onTempoSliderChange}
          onTempoSliderChangeCommitted={this.onTempoSliderChangeCommitted}
          onDurationSliderChange={this.onDurationSliderChange}
          onDurationSliderChangeCommitted={this.onDurationSliderChangeCommitted}
          handleSelectTagsChange={this.handleSelectTagsChange}
          tempoRangeArray={tempoRangeArray}
          durationRangeArray={durationRangeArray}
          selectedKey={key}
          selectedMode={mode}
          selectedSortBy={sortBy}
          selectedAdditionalTagOptions={additional_tags_options}
          selectedExcludeTagOptions={exclude_tags_options}
          shortestSampleDuration={shortestSampleDuration}
          longestSampleDuration={longestSampleDuration}
        />
        <DividerContainer>
          <div className="divider"></div>
          <Fade in={isLoading}>
            <LinearProgress color="primary" />
          </Fade>
        </DividerContainer>
        {collections && collections.length > 0 && shouldShowCollections && (
          <div className="section">
            <CollectionsCarouselContainer>
              <h4>Collections</h4>
              <Carousel collections={collections} />
            </CollectionsCarouselContainer>
          </div>
        )}
        <div className="section">
          <Samples pagyMetadata={pagyMetadata} showSampleSelect={showSampleSelect} />
        </div>
      </Provider>
    )
  }
}
